import React from "react";
import { useTranslation } from "react-i18next";
import { Grid, Link, Typography } from "@mui/material";
import {
  Calendar,
  ClipboardText,
  Files,
  IdentificationBadge,
  UsersThree,
} from "@phosphor-icons/react";

import timerSvg from "assets/timer.svg";
import { historyIcon } from "../styled";
import { useGetDocumentById } from "hooks/useGetDocumentById";

/**
 * The HistoryItem component displays information about the type and value of a historical event.
 * @component `HistoryItem`
 * @param {Object} props - The props of the component.
 * @param {String} props.type - The type of the historical event.
 * @param {String} props.value - The value of the historical event.
 * @return {JSX.Element} The `HistoryItem` component is being returned.
 */
export type HistoryItemType =
  | "uploadedBy"
  | "uploadedDate"
  | "modifiedBy"
  | "modifiedOn"
  | "administrationDate"
  | "administratorName"
  | "respondentName"
  | "relationshipToClient"
  | "otherRelationshipToClient"
  | "reportedSymptomSeverity"
  | "formAdministered"
  | "observationSettings"
  | "lengthOfObservation";

interface HistoryItemProps {
  type: HistoryItemType;
  value: string;
}

export function HistoryItem({ type, value }: HistoryItemProps) {
  const { t } = useTranslation(["clientHeader"]);
  const typeLabel = {
    uploadedBy: t("clientHeader:assessments.historyLabels.uploadedBy"),
    uploadedDate: t("clientHeader:assessments.historyLabels.uploadedDate"),
    modifiedBy: t("clientHeader:assessments.historyLabels.modifiedBy"),
    modifiedOn: t("clientHeader:assessments.historyLabels.modifiedOn"),
    administrationDate: t("clientHeader:assessments.historyLabels.administrationDate"),
    administratorName: t("clientHeader:assessments.historyLabels.administratorName"),
    respondentName: t("clientHeader:assessments.historyLabels.respondentName"),
    relationshipToClient: t("clientHeader:assessments.historyLabels.relationshipToClient"),
    otherRelationshipToClient: t(
      "clientHeader:assessments.historyLabels.otherRelationshipToClient"
    ),
    reportedSymptomSeverity: t("clientHeader:assessments.historyLabels.reportedSymptomSeverity"),
    formAdministered: t("clientHeader:assessments.historyLabels.formAdministered"),
    observationSettings: t("clientHeader:assessments.historyLabels.observationSettings"),
    lengthOfObservation: t("clientHeader:assessments.historyLabels.lengthOfObservation"),
  };

  const isIdentityType =
    type === "uploadedBy" ||
    type === "modifiedBy" ||
    type === "administratorName" ||
    type === "respondentName";

  const isCalendarType =
    type === "uploadedDate" || type === "modifiedOn" || type === "administrationDate";

  const isRelationshipType =
    type === "relationshipToClient" || type === "otherRelationshipToClient";

  const isTimerType = type === "observationSettings" || type === "lengthOfObservation";

  return (
    <>
      <Grid item xs={6}>
        <Typography component="p" variant="body2">
          <Typography component="span" variant="body2" sx={historyIcon}>
            {isTimerType ? (
              <img src={timerSvg} alt="" style={{ width: 18, height: 18 }} />
            ) : isIdentityType ? (
              <IdentificationBadge weight="duotone" size="18px" />
            ) : isCalendarType ? (
              <Calendar weight="duotone" size="18px" />
            ) : isRelationshipType ? (
              <UsersThree weight="duotone" size="18px" />
            ) : (
              <ClipboardText weight="duotone" size="18px" />
            )}
          </Typography>

          {typeLabel[type]}
        </Typography>
      </Grid>

      <Grid item xs={6}>
        <Typography component="h3" variant="subtitle2" align="right">
          {value}
        </Typography>
      </Grid>
    </>
  );
}

interface DocumentLinkItemProps {
  label: string;
  documentId: number;
  currentUserId: string | number;
}

export function DocumentLinkItem({ label, documentId, currentUserId }: DocumentLinkItemProps) {
  const { data: document } = useGetDocumentById(documentId);

  if (!document) return null;

  const url = `/document-viewer/${currentUserId}/${documentId}`;
  const displayName = document.display_filename || document.filename || "View document";

  return (
    <>
      <Grid item xs={6}>
        <Typography component="p" variant="body2">
          <Typography component="span" variant="body2" sx={historyIcon}>
            <Files weight="duotone" size="18px" />
          </Typography>
          {label}
        </Typography>
      </Grid>

      <Grid item xs={6}>
        <Link
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          display="block"
          sx={{ textAlign: "right" }}
        >
          {displayName}
        </Link>
      </Grid>
    </>
  );
}

interface AddDocumentLinkItemProps {
  label: string;
  onClick: () => void;
}

export function AddDocumentLinkItem({ label, onClick }: AddDocumentLinkItemProps) {
  return (
    <>
      <Grid item xs={6}>
        <Typography component="p" variant="body2">
          <Typography component="span" variant="body2" sx={historyIcon}>
            <Files weight="duotone" size="18px" />
          </Typography>
          {label}
        </Typography>
      </Grid>

      <Grid item xs={6}>
        <Link
          component="button"
          variant="body2"
          display="block"
          sx={{
            textAlign: "right",
            cursor: "pointer",
            background: "none",
            border: "none",
            p: 0,
            width: "100%",
          }}
          onClick={onClick}
        >
          Add {label}
        </Link>
      </Grid>
    </>
  );
}
